import { FC } from 'react'
import styles from './AutopilotSteps.module.scss'
import { AutopilotStepsProps } from './types'
import { useRootStore } from 'components/_hooks/useRootStore'
import { observer } from 'mobx-react'
import { Img, ImgBackground } from 'components/basic/Img'
import { useSlider } from 'components/_hooks/useSlider'
import { IconMap } from 'components/_const_assets/icons'
import cx from 'classnames'
import { VideoGif } from 'components/basic/VideoGif'
import Snore from 'components/Phantom/Icon/icons/dark/Snoring'

export const AutopilotStepsConnected = observer(() => {
	const { settingsStore } = useRootStore()
	return <AutopilotSteps metric={settingsStore.metricRegion} />
})

const ArrowLeftCircle = ({ className, onClick }) => (
	<svg
		width="50"
		height="50"
		viewBox="0 0 50 50"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
		onClick={onClick}
	>
		<circle
			cx="25"
			cy="25"
			r="25"
			fill="#39393b"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M26.453 34.032C26.5635 34.1937 26.709 34.3283 26.8787 34.426C27.0484 34.5236 27.2379 34.5818 27.4332 34.5961C27.6284 34.6104 27.8244 34.5805 28.0065 34.5086C28.1886 34.4367 28.3522 34.3247 28.485 34.1808L28.605 34.0528C28.8318 33.8067 28.9693 33.4914 28.9954 33.1577C29.0216 32.8241 28.9348 32.4913 28.749 32.2128L22.813 24.9552L28.749 17.7872C28.9348 17.5088 29.0216 17.176 28.9954 16.8423C28.9693 16.5086 28.8318 16.1934 28.605 15.9472L28.485 15.8192C28.3522 15.6754 28.1886 15.5634 28.0065 15.4915C27.8244 15.4196 27.6284 15.3897 27.4332 15.404C27.2379 15.4183 27.0484 15.4764 26.8787 15.5741C26.709 15.6717 26.5635 15.8064 26.453 15.968L19.6738 24.0928C19.4958 24.3452 19.4002 24.6464 19.4002 24.9552C19.4002 25.264 19.4958 25.5653 19.6738 25.8176L26.453 34.032Z"
			fill="#d2d2d3"
		/>
	</svg>
)

const ArrowRightCircle = ({ className, onClick }) => (
	<svg
		width="50"
		height="50"
		viewBox="0 0 50 50"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
		onClick={onClick}
	>
		<circle
			cx="25"
			cy="25"
			r="25"
			fill="#39393b"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M23.547 15.968C23.4365 15.8063 23.291 15.6717 23.1213 15.574C22.9516 15.4764 22.7621 15.4182 22.5668 15.4039C22.3716 15.3896 22.1756 15.4195 21.9935 15.4914C21.8114 15.5633 21.6478 15.6753 21.515 15.8192L21.395 15.9472C21.1682 16.1933 21.0307 16.5086 21.0046 16.8423C20.9784 17.1759 21.0652 17.5087 21.251 17.7872L27.187 25.0448L21.251 32.2128C21.0652 32.4912 20.9784 32.824 21.0046 33.1577C21.0307 33.4914 21.1682 33.8066 21.395 34.0528L21.515 34.1808C21.6478 34.3246 21.8114 34.4366 21.9935 34.5085C22.1756 34.5804 22.3716 34.6103 22.5668 34.596C22.7621 34.5817 22.9516 34.5236 23.1213 34.4259C23.291 34.3283 23.4365 34.1936 23.547 34.032L30.3262 25.9072C30.5042 25.6548 30.5998 25.3536 30.5998 25.0448C30.5998 24.736 30.5042 24.4347 30.3262 24.1824L23.547 15.968Z"
			fill="#d2d2d3"
		/>
	</svg>
)

const StepOne = () => {
	const [sliderRef, currentSlide, goToSlide] = useSlider({
		throwResistance: 3000,
		rightBoundsCushionMultiplier: 0.4,
	})
	const slidesLength = 10

	const onPrevClick = () => {
		goToSlide(Math.max(0, currentSlide - 1))
	}
	const onNextClick = () => {
		goToSlide(Math.min(slidesLength - 1, currentSlide + 1))
	}

	return (
		<>
			<article className={`${styles.step} ${styles.mobile_reverse} inner-paddings-x inner-paddings-y max-1440`}>
				<div className={styles.step_content}>
					<div className={styles.step_label}>Step 1</div>
					<h3 className={styles.step_h3}>Autopilot tracks your sleep and health. No wearable required.</h3>
				</div>
				<div className={styles.step_content}>
					<p className={styles.step_p}>For up to two sleepers at a time, Autopilot runs complex algorithms from the data collected from the Pod that compute metrics including the following. </p>
				</div>
			</article>
			<article className={`inner-paddings-x inner-paddings-bottom max-1440 ${styles.slider_wrapper}`}>
				<ul
					className={styles.slider}
					ref={sliderRef}
				>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_deep_asleep}
							alt=""
						/>
						<p>Time spent in deep sleep</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_rem_sleep}
							alt=""
						/>
						<p>Time spent in REM sleep</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_light_sleep}
							alt=""
						/>
						<p>Time spent in light sleep</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_clock}
							alt=""
						/>
						<p>Time you slept</p>
					</li>
					<li className={styles.slide}>
						<Snore
							color="white"
							className={styles.slide_img}
						/>
						<p>Snoring detection</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_fell_asleep}
							alt=""
						/>
						<p>Time you fell asleep</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_alarm}
							alt=""
						/>
						<p>Time you woke up</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_bed}
							alt=""
						/>
						<p>Time you got into & out of bed</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_heart_report}
							alt=""
						/>
						<p>Heart Rate Variability (HRV)</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_heart}
							alt=""
						/>
						<p>Resting heart rate</p>
					</li>
					<li className={styles.slide}>
						<Img
							src={IconMap.autopilot_lung}
							alt=""
						/>
						<p>Respiratory rate</p>
					</li>
				</ul>
				<div className={styles.nav}>
					<ArrowLeftCircle
						className={cx(styles.nav_button, { [styles.nav_button_disabled]: currentSlide === 0 })}
						onClick={onPrevClick}
					/>
					<ArrowRightCircle
						className={cx(styles.nav_button, { [styles.nav_button_disabled]: currentSlide === slidesLength - 1 })}
						onClick={onNextClick}
					/>
				</div>
			</article>
		</>
	)
}

const StepTwo = () => {
	return (
		<article className={`${styles.step} ${styles.mobile_reverse} inner-paddings-x inner-paddings-y max-1440`}>
			<div className={styles.step_image}>
				<VideoGif src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-two-animation.mp4" />
			</div>
			<div className={styles.step_content}>
				<div className={styles.step_label}>Step 2</div>
				<h3 className={styles.step_h3}>Autopilot learns more about you over time</h3>
				<p className={styles.step_p}>Once your initial blueprint is created, Autopilot continuously learns more about you, making subtle adjustments with any changes that arise.</p>
			</div>
		</article>
	)
}

const StepThree = () => {
	return (
		<article className={`${styles.step} inner-paddings-x inner-paddings-y max-1440`}>
			<div className={styles.step_content}>
				<div className={styles.step_label}>Step 3</div>
				<h3 className={styles.step_h3}>Autopilot makes real-time adjustments to give you deeper sleep</h3>
				<p className={styles.step_p}>
					Your body temperature fluctuates naturally throughout the night. Autopilot determines your optimal sleep temperature at every point of the night and makes automatic adjustments to your Pod
					by cooling or warming each side, without any manual effort from you.
				</p>
			</div>
			<div className={styles.step_image}>
				<Img
					src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-three-2024.png"
					alt=""
				/>
			</div>
		</article>
	)
}

const StepFour = () => {
	return (
		<article className={`${styles.step} inner-paddings-x inner-paddings-y max-1440`}>
			<div className={styles.step_image}>
				<Img
					src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-phantom-launch/autopilot-elevation-step.png"
					sources={[
						{
							src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-phantom-launch/8Sleep_13_02_24122028BLUE+1(1).png',
							mediaQuery: '(max-width: 599px)',
							dprHeight: 1080,
						},
						{
							src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-phantom-launch/autopilot-elevation-step.png',
							mediaQuery: '(min-width: 600px)',
							dprHeight: 1080,
						},
					]}
					alt=""
				/>
				<Img
					className={styles.step_card_img}
					src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/2024-phantom-launch/Snore+card.svg"
					alt="Snore card"
					simple
					unoptimized
				/>
			</div>
			<div className={styles.step_content}>
				<div className={styles.step_label}>Step 4</div>
				<h3 className={styles.step_h3}>Autopilot adjusts your elevation to stop snoring</h3>
				<p className={styles.step_p}>
					Autopilot will make automatic elevation adjustment to reduce snoring, exclusive to Pod 4 Ultra. It will also go to your sleeping elevation automatically at bedtime.
				</p>
			</div>
		</article>
	)
}

const StepFive = () => {
	return (
		<ImgBackground
			src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-four-desktop-2024.jpg"
			alt=""
			sources={[
				{
					src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-four-mobile-2024.png',
					mediaQuery: '(max-width: 599px)',
					dprHeight: 1080,
				},
				{
					src: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-four-desktop-2024.jpg',
					mediaQuery: '(min-width: 600px)',
					dprHeight: 1080,
				},
			]}
			className={`${styles.step_background} outer-margins-y`}
		>
			<article className={`${styles.special_step} inner-paddings-bottom inner-paddings-x`}>
				<div className={styles.step_label}>Step 5</div>
				<h3 className={styles.step_h3}>Autopilot wakes you up gently and more refreshed</h3>
				<p className={styles.step_p}>Autopilot wakes you up with a gentle vibration and thermal alarm. This experience is designed to wake you up refreshed without disturbing your partner.</p>
			</article>
		</ImgBackground>
	)
}

const StepSix = () => {
	return (
		<article className={`${styles.step} inner-paddings-x max-1440`}>
			<div className={styles.step_content}>
				<div className={styles.step_label}>Step 6</div>
				<h3 className={styles.step_h3}>Autopilot creates a daily report so you can track your sleep progress</h3>
				<p className={styles.step_p}>
					In the morning, Autopilot sends your sleep and health data to the app. Your metrics are summarized into a daily report to help you stay on top of your overall health.
				</p>
				<div className={styles.step_explainer}>
					<Img
						src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-five-explainer.png"
						alt=""
					/>
				</div>
			</div>
			<div className={styles.step_image}>
				<Img
					src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/autopilot-step-five-widget.png"
					alt=""
					className={styles.step5_image}
				/>
			</div>
		</article>
	)
}

const StepSeven = () => {
	return (
		<article className={`${styles.step} ${styles.mobile_reverse} inner-paddings-x inner-paddings-y max-1440`}>
			<div className={styles.step_image}>
				<VideoGif src="https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/glasslock.mp4" />
			</div>
			<div className={styles.step_content}>
				<div className={styles.step_label}>Step 7</div>
				<h3 className={styles.step_h3}>Autopilot gives you a complete view of your sleep and health</h3>
				<p className={styles.step_p}>
					Every time you sleep on the Pod, Autopilot captures all your key sleep and health information and encrypts and stores it securely in the cloud. You can always refer to your sleep report to
					see your historical health trends to learn more about yourself.
				</p>
			</div>
		</article>
	)
}

export const AutopilotSteps: FC<AutopilotStepsProps> = (props) => {
	const { metric } = props
	return (
		<section className={`${styles.container} inner-paddings-y`}>
			<StepOne />
			<StepTwo />
			<StepThree />
			<StepFour />
			<StepFive />
			<StepSix />
			<StepSeven />
		</section>
	)
}
